import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import {
  Check, MessageSquare, Zap, Star, LayoutDashboard,
  Inbox, ArrowRight, ChevronRight, Clock
} from "lucide-react";
import { FadeUp, CardHover, useInView } from "../hooks/motion";
import { CALCOM_DEMO_URL } from "../config/demo";
import { PUBLIC_AUTH_ENABLED, getAuthSignInURL, getAuthSignUpURL, SIGNIN_EVENT, SIGNUP_EVENT } from "../config/auth";

/* ── SMS Mockup ─────────────────────────────────────────── */
const SMS_MESSAGES = [
  { id: 1, type: "in", text: "Hey, can I get a quote for a deep clean — 2,100 sqft?" },
  { id: 2, type: "out", text: "On it! Here's your quote:" },
  { id: 3, type: "quote" },
  { id: 4, type: "in", text: "Looks perfect. I'll take it!" },
  { id: 5, type: "status", text: "Job Created", color: "green" },
  { id: 6, type: "out", text: "Job's done! Mind leaving us a quick review?" },
  { id: 7, type: "stars" },
];
const DELAYS = [400, 1400, 2400, 3600, 4400, 5600, 6600];

const SMSMockup = () => {
  const [visible, setVisible] = useState(0);
  const prefersReduced = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    if (prefersReduced.current) { setVisible(SMS_MESSAGES.length); return; }
    if (visible >= SMS_MESSAGES.length) {
      const t = setTimeout(() => setVisible(0), 2500);
      return () => clearTimeout(t);
    }
    const delay = visible === 0 ? DELAYS[0] : DELAYS[visible] - DELAYS[visible - 1];
    const t = setTimeout(() => setVisible((v) => v + 1), delay);
    return () => clearTimeout(t);
  }, [visible]);

  return (
    <div data-testid="sms-mockup" className="relative mx-auto w-full max-w-[300px]">
      {/* Device shell — fixed dimensions, never resizes during animation */}
      <div className="relative bg-[#050507] rounded-[2.75rem] p-2.5 border border-[#26263a] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.85)]">
        <div className="pointer-events-none absolute inset-0 rounded-[2.75rem] ring-1 ring-inset ring-white/[0.04]" />
        {/* Screen — fixed height reserved immediately */}
        <div
          className="relative rounded-[2.25rem] bg-[#0d0d14] overflow-hidden flex flex-col"
          style={{ height: "460px" }}
        >
          {/* Status bar with notch */}
          <div className="relative h-9 flex-shrink-0 flex items-center justify-center">
            <div className="h-6 w-28 bg-black rounded-full flex items-center justify-center gap-2">
              <span className="w-8 h-1 rounded-full bg-[#1E1E2E]" />
              <span className="w-2 h-2 rounded-full bg-[#1E1E2E]" />
            </div>
          </div>

          {/* Header */}
          <div className="flex items-center gap-2 px-4 pb-3 border-b border-[#1E1E2E] flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-[#71DED6]/20 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-[#71DED6]" />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#F4F4F8]">CleanBid SMS</p>
              <p className="text-[10px] text-[#71DED6]">Business Line</p>
            </div>
          </div>

          {/* Message viewport — fixed area, content anchored to bottom, clips upward */}
          <div className="relative flex-1 overflow-hidden">
            <div className="pointer-events-none absolute top-0 inset-x-0 h-8 z-10 bg-gradient-to-b from-[#0d0d14] to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-4 space-y-2.5">
              {SMS_MESSAGES.slice(0, visible).map((msg) => (
                <div
                  key={msg.id}
                  style={{ animation: "message-in 0.35s cubic-bezier(0.22,1,0.36,1) forwards" }}
                >
                  {msg.type === "in" && (
                    <div className="flex justify-start">
                      <div className="bg-[#1E1E2E] rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%]">
                        <p className="text-xs text-[#F4F4F8]">{msg.text}</p>
                      </div>
                    </div>
                  )}
                  {msg.type === "out" && (
                    <div className="flex justify-end">
                      <div className="bg-[#6B5CF6]/30 border border-[#6B5CF6]/20 rounded-2xl rounded-tr-sm px-3 py-2 max-w-[85%]">
                        <p className="text-xs text-[#F4F4F8]">{msg.text}</p>
                      </div>
                    </div>
                  )}
                  {msg.type === "quote" && (
                    <div className="flex justify-end">
                      <div className="bg-[#111118] border border-[#71DED6]/30 rounded-2xl rounded-tr-sm px-3 py-2.5 max-w-[90%] w-full">
                        <p className="text-[10px] text-[#8B8BA7] uppercase tracking-wider mb-1">Quote</p>
                        <p className="text-xs font-semibold text-[#F4F4F8]">Deep Clean Package</p>
                        <p className="text-[10px] text-[#8B8BA7]">2,100 sqft · Thu Jun 5</p>
                        <p className="text-lg font-bold text-[#71DED6] mt-1">$285</p>
                        <button className="mt-2 w-full bg-[#71DED6] text-[#10151D] text-[10px] font-bold py-1.5 rounded-full">
                          Accept Quote
                        </button>
                      </div>
                    </div>
                  )}
                  {msg.type === "status" && (
                    <div className="flex justify-center">
                      <span className="bg-[#71DED6]/15 border border-[#71DED6]/30 text-[#71DED6] text-[10px] font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" /> {msg.text}
                      </span>
                    </div>
                  )}
                  {msg.type === "stars" && (
                    <div className="flex justify-start">
                      <div className="bg-[#1E1E2E] rounded-2xl rounded-tl-sm px-3 py-2">
                        <div className="flex gap-0.5 mb-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                          ))}
                        </div>
                        <p className="text-[10px] text-[#8B8BA7]">Amazing service, will book again!</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating chips — absolute to the fixed phone wrapper, never affect page flow */}
      <div className="absolute -top-3 -right-4 bg-[#111118] border border-[#71DED6]/30 rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-glow-green z-10">
        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
        <span className="text-[10px] font-semibold text-[#F4F4F8]">12 reviews this week</span>
      </div>
      <div className="absolute -bottom-2 -left-4 bg-[#111118] border border-[#6B5CF6]/30 rounded-full px-3 py-1.5 flex items-center gap-1.5 z-10">
        <Zap className="w-3 h-3 text-[#6B5CF6]" />
        <span className="text-[10px] font-semibold text-[#F4F4F8]">Follow-up sent</span>
      </div>
    </div>
  );
};

/* ── Animated stat ──────────────────────────────────────── */
const AnimatedStat = ({ value, suffix = "", prefix = "", label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const num = parseFloat(String(value).replace(/[^0-9.]/g, ""));
    const steps = 60;
    const inc = num / steps;
    let cur = 0;
    const isDecimal = String(value).includes(".");
    const timer = setInterval(() => {
      cur += inc;
      if (cur >= num) { setCount(num); clearInterval(timer); }
      else setCount(isDecimal ? parseFloat(cur.toFixed(1)) : Math.floor(cur));
    }, 1200 / steps);
    return () => clearInterval(timer);
  }, [inView, value]);
  return (
    <div ref={ref} className="text-center">
      <p className="text-4xl md:text-5xl font-bold text-[#F4F4F8] tracking-tight">
        {prefix}{count}{suffix}
      </p>
      <p className="text-[#8B8BA7] text-sm mt-2 max-w-[160px] mx-auto">{label}</p>
    </div>
  );
};

/* ── Feature Mock UIs ───────────────────────────────────── */
const QuoteMock = () => (
  <div className="bg-[#0A0A0F] rounded-2xl border border-[#1E1E2E] p-5 space-y-3">
    <div className="flex items-center gap-2 pb-3 border-b border-[#1E1E2E]">
      <div className="w-2 h-2 rounded-full bg-[#71DED6]" />
      <span className="text-xs text-[#8B8BA7]">New message from Sarah K.</span>
      <span className="ml-auto text-xs text-[#8B8BA7]">2m ago</span>
    </div>
    <div className="flex justify-start">
      <div className="bg-[#1E1E2E] rounded-xl px-3 py-2 text-xs text-[#F4F4F8] max-w-[80%]">
        I need a quote for move-out cleaning
      </div>
    </div>
    <div className="flex justify-end">
      <div className="bg-[#111118] border border-[#71DED6]/40 rounded-xl px-3 py-2 max-w-[90%] w-full">
        <p className="text-[10px] text-[#8B8BA7] mb-1">Move-Out Clean · 1,800 sqft</p>
        <p className="text-lg font-bold text-[#71DED6]">$245</p>
        <div className="mt-2 bg-[#71DED6] text-[#10151D] text-[10px] font-bold py-1 px-3 rounded-full text-center">
          Accept Quote
        </div>
      </div>
    </div>
    <div className="text-center">
      <span className="text-[10px] text-[#8B8BA7]">Sent 60 seconds after inquiry</span>
    </div>
  </div>
);

const FollowUpMock = () => (
  <div className="bg-[#0A0A0F] rounded-2xl border border-[#1E1E2E] p-5 space-y-2.5">
    <p className="text-xs text-[#8B8BA7] uppercase tracking-widest mb-3">Follow-up Sequence</p>
    {[
      { label: "Day 0: Quote sent", status: "sent", color: "green" },
      { label: "Day 1: Friendly reminder", status: "sent", color: "indigo" },
      { label: "Day 3: Value follow-up", status: "sent", color: "indigo" },
      { label: "Day 5: Final nudge", status: "scheduled", color: "muted" },
    ].map((step, i) => (
      <div key={i} className="flex items-center gap-3">
        <div
          className={`w-2 h-2 rounded-full flex-shrink-0 ${
            step.color === "green" ? "bg-[#71DED6]" :
            step.color === "indigo" ? "bg-[#6B5CF6]" : "bg-[#1E1E2E] border border-[#8B8BA7]"
          }`}
        />
        <span className="text-xs text-[#F4F4F8] flex-1">{step.label}</span>
        <span className={`text-[10px] font-medium ${
          step.status === "sent" ? "text-[#71DED6]" : "text-[#8B8BA7]"
        }`}>
          {step.status === "sent" ? "Sent" : "Scheduled"}
        </span>
      </div>
    ))}
    <div className="pt-2 border-t border-[#1E1E2E]">
      <span className="text-[10px] text-[#8B8BA7]">Open rate: </span>
      <span className="text-[10px] text-[#71DED6] font-semibold">82%</span>
    </div>
  </div>
);

const ReviewMock = () => (
  <div className="bg-[#0A0A0F] rounded-2xl border border-[#1E1E2E] p-5 space-y-3">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-2 h-2 rounded-full bg-[#71DED6] animate-pulse" />
      <span className="text-xs text-[#8B8BA7]">Review request sent automatically</span>
    </div>
    {[
      { name: "Jake M.", stars: 5, text: "Incredible job, so thorough!" },
      { name: "Maria S.", stars: 5, text: "Best cleaning service I've used." },
      { name: "Derek P.", stars: 5, text: "Will book again for sure." },
    ].map((r, i) => (
      <div key={i} className="flex items-start gap-2">
        <div className="w-6 h-6 rounded-full bg-[#6B5CF6]/30 flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-[#6B5CF6]">
          {r.name[0]}
        </div>
        <div>
          <div className="flex gap-0.5 mb-0.5">
            {[...Array(r.stars)].map((_, j) => (
              <Star key={j} className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <p className="text-[10px] text-[#8B8BA7]">{r.text}</p>
        </div>
      </div>
    ))}
  </div>
);

const JobTrackMock = () => (
  <div className="bg-[#0A0A0F] rounded-2xl border border-[#1E1E2E] p-5">
    <p className="text-xs text-[#8B8BA7] uppercase tracking-widest mb-3">Pipeline</p>
    <div className="grid grid-cols-3 gap-2">
      {[
        { col: "Leads", jobs: ["Sarah K.", "Tom B."], color: "indigo" },
        { col: "Quoted", jobs: ["Mike R."], color: "yellow" },
        { col: "Booked", jobs: ["Amanda L.", "Chris P."], color: "green" },
      ].map((col) => (
        <div key={col.col}>
          <p className={`text-[10px] font-semibold mb-2 ${
            col.color === "green" ? "text-[#71DED6]" :
            col.color === "indigo" ? "text-[#6B5CF6]" : "text-amber-400"
          }`}>
            {col.col}
          </p>
          {col.jobs.map((j) => (
            <div key={j} className="bg-[#1E1E2E] rounded-lg px-2 py-1.5 mb-1.5">
              <p className="text-[10px] text-[#F4F4F8]">{j}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const InboxMock = () => (
  <div className="bg-[#0A0A0F] rounded-2xl border border-[#1E1E2E] p-5 space-y-2">
    <p className="text-xs text-[#8B8BA7] uppercase tracking-widest mb-3">Unified Inbox</p>
    {[
      { name: "Sarah K.", msg: "Accepted your quote!", time: "2m", dot: "green" },
      { name: "Tom B.", msg: "Can you do Friday?", time: "15m", dot: "indigo" },
      { name: "Amanda L.", msg: "Thanks! Great service", time: "1h", dot: "amber" },
      { name: "Chris P.", msg: "Need a quote for...", time: "3h", dot: "muted" },
    ].map((item) => (
      <div key={item.name} className="flex items-center gap-3 py-2 border-b border-[#1E1E2E] last:border-0">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
          item.dot === "green" ? "bg-[#71DED6]" :
          item.dot === "indigo" ? "bg-[#6B5CF6]" :
          item.dot === "amber" ? "bg-amber-400" : "bg-[#1E1E2E]"
        }`} />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[#F4F4F8]">{item.name}</p>
          <p className="text-[10px] text-[#8B8BA7] truncate">{item.msg}</p>
        </div>
        <span className="text-[10px] text-[#8B8BA7] flex-shrink-0">{item.time}</span>
      </div>
    ))}
  </div>
);

/* ── Feature rows ───────────────────────────────────────── */
const FEATURES = [
  {
    badge: "SMS Quoting",
    headline: "Send a quote in 60 seconds. Get a response in minutes.",
    body: "Customers expect speed. Build a quote inside CleanBid, send it via text, and watch acceptances come back before your competitors even pick up the phone.",
    visual: <QuoteMock />,
  },
  {
    badge: "Follow-Up Automation",
    headline: "The follow-up you never send is the job you don't win.",
    body: "Most leads need 2–3 touches. CleanBid runs your follow-up sequences automatically — friendly, timed, and completely hands-off for you.",
    visual: <FollowUpMock />,
  },
  {
    badge: "Review Automation",
    headline: "Turn every completed job into a 5-star review.",
    body: "The moment you mark a job done, CleanBid sends a review request via SMS. No chasing. No asking awkwardly in person. Just reviews.",
    visual: <ReviewMock />,
  },
  {
    badge: "Job Tracking",
    headline: "Lead to completion in one view.",
    body: "See every job at a glance — who's a lead, who got a quote, who's booked. Your whole business in one clean pipeline.",
    visual: <JobTrackMock />,
  },
  {
    badge: "Unified Inbox",
    headline: "Every conversation. One screen.",
    body: "Stop bouncing between apps. All your customer texts live in one place so nothing slips through the cracks.",
    visual: <InboxMock />,
  },
];

/* ── Service cards data ──────────────────────────────────── */
const SERVICE_CARDS = [
  { img: "https://cdn.searchlegends.com/kro-website/cleaning-services.jpg", label: "House Cleaning", alt: "Professional house cleaning technician" },
  { img: "https://cdn.searchlegends.com/kro-website/lawn-care.webp", label: "Lawn Care", alt: "Professional lawn care technician" },
  { img: "https://cdn.searchlegends.com/kro-website/pest-control.jpg", label: "Pest Control", alt: "Professional pest control technician" },
  { img: "https://cdn.searchlegends.com/kro-website/gutter-cleaner.jpg", label: "Gutter & Exterior", alt: "Professional gutter cleaning technician" },
  { img: "https://cdn.searchlegends.com/kro-website/roofing.jpg", label: "Roofing", alt: "Professional roofing installer" },
  { img: "https://cdn.searchlegends.com/kro-website/powerwashing.jpg", label: "Power Washing", alt: "Professional power washing technician" },
  { img: "https://cdn.searchlegends.com/kro-website/window-cleaner.jpg", label: "Window Cleaning", alt: "Professional window cleaning technician" },
  { img: "https://cdn.searchlegends.com/kro-website/pool-cleaner.jpg", label: "Pool Services", alt: "Professional pool service technician" },
  { img: "https://cdn.searchlegends.com/kro-website/snow-removal.jpg", label: "Snow Removal", alt: "Professional snow removal service" },
  { img: "https://cdn.searchlegends.com/kro-website/handyman.jpg", label: "Handyman", alt: "Professional handyman technician" },
];

const ServiceCard = ({ card, index }) => (
  <div
    data-testid={`service-card-${index}`}
    className="relative flex-shrink-0 rounded-2xl overflow-hidden border border-transparent hover:border-[#71DED6]/40 transition-[border-color] duration-300 cursor-default"
    style={{ width: "220px", height: "280px", marginRight: "16px" }}
  >
    <img
      src={card.img}
      alt={card.alt}
      loading="lazy"
      className="w-full h-full object-cover object-top"
    />
    <div
      className="absolute inset-x-0 bottom-0 pointer-events-none"
      style={{ height: "60%", background: "linear-gradient(to top, rgba(10,10,15,0.9) 0%, transparent 60%)" }}
    />
    <p className="absolute bottom-0 left-0 right-0 text-white font-semibold text-sm p-4">{card.label}</p>
  </div>
);

/* ── Main component ─────────────────────────────────────── */
export default function HomePage() {
  usePageTitle("CleanBid.ai — From First Lead to Final Payment");
  const prefersReducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return (
    <div className="bg-[#0A0A0F]">
      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-24 overflow-hidden hero-grid">
        <div className="absolute inset-0 glow-cta pointer-events-none" />
        {/* Brand mark watermark — decorative depth element, behind all hero content */}
        <img
          src="/cleanbid-logo-transparent.png"
          alt=""
          aria-hidden="true"
          className="absolute right-0 z-0 pointer-events-none select-none w-[280px] h-[280px] md:w-[480px] md:h-[480px] opacity-[0.05] md:opacity-[0.06]"
          style={{ top: "50%", transform: "translateY(-50%) rotate(8deg)" }}
        />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left */}
            <div className="space-y-6">
              <FadeUp>
                <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">
                  Software for Home Service Businesses
                </span>
              </FadeUp>
              <FadeUp delay={100}>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05] text-[#F4F4F8]">
                  From first lead to{" "}
                  <span className="text-gradient-green">final payment.</span>
                </h1>
              </FadeUp>
              <FadeUp delay={200}>
                <p className="text-lg text-[#8B8BA7] leading-relaxed max-w-xl">
                  CleanBid.ai keeps your leads, quotes, scheduled work, jobs, invoices, payments, and customer follow-up organized in one place.
                </p>
              </FadeUp>
              <FadeUp delay={350}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={CALCOM_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-cta-primary"
                    className="bg-[#71DED6] text-[#10151D] font-semibold px-8 py-3.5 rounded-full text-base hover:scale-[1.02] hover:shadow-glow-green-sm transition-transform duration-200 text-center"
                  >
                    Book a Demo
                  </a>
                  <Link
                    to="/how-it-works"
                    data-testid="hero-cta-secondary"
                    className="border border-[#1E1E2E] text-[#F4F4F8] font-semibold px-8 py-3.5 rounded-full text-base hover:bg-[#111118] transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    See How It Works <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </FadeUp>
              <FadeUp delay={450}>
                <p className="text-sm text-[#8B8BA7]">
                  <span className="text-[#F4F4F8] font-medium">Used by 200+ service businesses.</span>{" "}
                  No free trials. No fluff. Just results.
                </p>
              </FadeUp>
              <FadeUp delay={520}>
                <p className="text-sm text-[#8B8BA7]">
                  {PUBLIC_AUTH_ENABLED ? (
                    <>
                      Already have an account?{" "}
                      <a
                        href={getAuthSignInURL()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-signin-link"
                        data-event={SIGNIN_EVENT}
                        className="text-[#71DED6] hover:underline font-medium"
                      >
                        Sign in →
                      </a>
                    </>
                  ) : (
                    "CleanBid is currently onboarding through guided demos."
                  )}
                </p>
              </FadeUp>
            </div>
            {/* Right — SMS mockup */}
            <FadeUp delay={500} y={40} className="flex justify-center lg:justify-end">
              <SMSMockup />
            </FadeUp>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-24 md:py-32" data-testid="problem-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] max-w-2xl">
              You're losing jobs you should be winning.
            </h2>
          </FadeUp>
          <FadeUp delay={80}>
            <p className="text-[#8B8BA7] text-lg mt-4 max-w-2xl leading-relaxed">
              Not because your service is bad. Because the follow-up didn't happen. The quote sat in your head instead of in their inbox. The review request never got sent.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {[
              {
                icon: <Clock className="w-6 h-6 text-[#8B8BA7]" />,
                title: "Slow Quotes",
                body: "Customers text you, wait hours, and book someone else.",
                testid: "problem-card-slow-quotes",
              },
              {
                icon: <MessageSquare className="w-6 h-6 text-[#8B8BA7]" />,
                title: "No Follow-Up",
                body: "Most leads need 2–3 touches. Most businesses send one and stop.",
                testid: "problem-card-no-followup",
              },
              {
                icon: <Star className="w-6 h-6 text-[#8B8BA7]" />,
                title: "Missing Reviews",
                body: "Great customers who never left reviews because nobody asked.",
                testid: "problem-card-missing-reviews",
              },
            ].map((card, i) => (
              <FadeUp key={i} delay={i * 100}>
                <CardHover>
                <div
                  data-testid={card.testid}
                  className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-7 transition-[box-shadow] duration-300 hover:shadow-card-hover cursor-default"
                >
                  <div className="mb-4">{card.icon}</div>
                  <h3 className="text-lg font-semibold text-[#F4F4F8] mb-2">{card.title}</h3>
                  <p className="text-[#8B8BA7] text-sm leading-relaxed">{card.body}</p>
                </div>
                </CardHover>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="py-24 md:py-32 bg-[#111118]/40" data-testid="solution-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <FadeUp>
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">How It Works</span>
          </FadeUp>
          <FadeUp delay={60}>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] max-w-2xl mt-3">
              A revenue system built for the field.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {[
              { num: "01", title: "Send the Quote", body: "Build in 60 seconds, send via SMS. Customers get a real quote, not a voicemail." },
              { num: "02", title: "Follow Up Automatically", body: "Sequences run until they respond. You don't lift a finger." },
              { num: "03", title: "Close the Job", body: "Accepted, created, tracked in one screen. Zero paperwork." },
              { num: "04", title: "Collect the Review", body: "Automatic request on job completion. 5 stars appear one by one." },
            ].map((step, i) => (
              <FadeUp key={i} delay={i * 100}>
                <CardHover>
                <div
                  data-testid={`solution-step-${i + 1}`}
                  className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-7 transition-[border-color] duration-300 hover:border-[#71DED6]/30"
                >
                  <span className="text-4xl font-bold text-[#1E1E2E] tracking-tighter">{step.num}</span>
                  <h3 className="text-lg font-semibold text-[#F4F4F8] mt-3 mb-2">{step.title}</h3>
                  <p className="text-[#8B8BA7] text-sm leading-relaxed">{step.body}</p>
                </div>
                </CardHover>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE HIGHLIGHTS */}
      <section className="py-24 md:py-32" data-testid="features-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] text-center mb-4">
              Every tool you need. Nothing you don't.
            </h2>
          </FadeUp>
          <div className="mt-16 space-y-20">
            {FEATURES.map((feat, i) => (
              <FadeUp key={i}>
                <div
                  data-testid={`feature-row-${i}`}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
                >
                  <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                    <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">{feat.badge}</span>
                    <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#F4F4F8] mt-3 mb-4">
                      {feat.headline}
                    </h3>
                    <p className="text-[#8B8BA7] leading-relaxed">{feat.body}</p>
                    <Link
                      to="/features"
                      className="inline-flex items-center gap-2 mt-6 text-sm text-[#71DED6] font-medium hover:gap-3 transition-all duration-200"
                    >
                      Learn more <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                  <div className={i % 2 === 1 ? "lg:order-1" : ""}>{feat.visual}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT PREVIEW */}
      <section className="py-24 md:py-28 overflow-hidden bg-[#111118]/30" data-testid="product-preview-section">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <FadeUp className="text-center mb-12">
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">The Platform</span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] mt-3">
              Your whole business in one screen.
            </h2>
          </FadeUp>
          <FadeUp>
            <div className="relative mx-auto max-w-4xl">
              {/* Main dashboard */}
              <div className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-6 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]">
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-[#1E1E2E]">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#1E1E2E]" />
                    <div className="w-3 h-3 rounded-full bg-[#1E1E2E]" />
                    <div className="w-3 h-3 rounded-full bg-[#1E1E2E]" />
                  </div>
                  <span className="text-xs text-[#8B8BA7] ml-2">CleanBid Dashboard</span>
                </div>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {[
                    { label: "Open Leads", val: "14", color: "indigo" },
                    { label: "Quotes Sent", val: "8", color: "yellow" },
                    { label: "Jobs Booked", val: "6", color: "green" },
                    { label: "Reviews", val: "47", color: "green" },
                  ].map((s) => (
                    <div key={s.label} className="bg-[#0A0A0F] rounded-xl p-4 border border-[#1E1E2E]">
                      <p className={`text-2xl font-bold ${s.color === "green" ? "text-[#71DED6]" : s.color === "indigo" ? "text-[#6B5CF6]" : "text-amber-400"}`}>
                        {s.val}
                      </p>
                      <p className="text-xs text-[#8B8BA7] mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {["New Lead", "Quote Sent", "Job Booked", "Completed", "Review Sent", ""].map((col, i) => (
                    i < 5 ? (
                      <div key={i} className="bg-[#0A0A0F] rounded-xl p-3 border border-[#1E1E2E]">
                        <p className="text-[10px] text-[#8B8BA7] mb-2">{col}</p>
                        {[1, 2].slice(0, i < 4 ? 2 : 1).map((j) => (
                          <div key={j} className="bg-[#1E1E2E] rounded-lg px-2 py-1.5 mb-1.5">
                            <div className="h-1.5 w-16 bg-[#2a2a3a] rounded" />
                          </div>
                        ))}
                      </div>
                    ) : null
                  ))}
                </div>
              </div>

              {/* Floating SMS thread */}
              <div className="hidden md:block absolute -right-8 top-8 w-64 bg-[#111118] border border-[#1E1E2E] rounded-2xl p-4 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.7)]">
                <p className="text-xs text-[#8B8BA7] mb-3 flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" /> SMS Thread
                </p>
                <div className="space-y-2">
                  <div className="bg-[#1E1E2E] rounded-xl px-3 py-1.5 text-[10px] text-[#F4F4F8]">
                    Can you do Thursday?
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-[#6B5CF6]/20 border border-[#6B5CF6]/20 rounded-xl px-3 py-1.5 text-[10px] text-[#F4F4F8]">
                      Yes! Quote sent ✓
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <span className="text-[#71DED6] text-[10px] font-semibold bg-[#71DED6]/10 px-2 py-0.5 rounded-full">
                      Job Created ✓
                    </span>
                  </div>
                </div>
              </div>

              {/* Floating chip */}
              <div className="absolute -left-6 bottom-8 hidden md:flex items-center gap-2 bg-[#111118] border border-[#71DED6]/30 rounded-full px-3 py-1.5 shadow-glow-green">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                <span className="text-xs text-[#F4F4F8] font-medium">Quote accepted ✓</span>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* SERVICES REEL */}
      <section className="py-24 md:py-28 overflow-hidden" data-testid="services-reel-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <FadeUp className="text-center mb-14">
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">Who CleanBid is built for</span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] mt-3">
              If you work in the field, CleanBid works for you.
            </h2>
          </FadeUp>
        </div>
        {prefersReducedMotion ? (
          /* Reduced motion: standard horizontal scroll with snap */
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-none" data-testid="services-reel">
              {SERVICE_CARDS.map((card, i) => (
                <ServiceCard key={i} card={card} index={i} />
              ))}
            </div>
          </div>
        ) : (
          /* Marquee: continuous full-width auto-scroll */
          <div className="overflow-hidden w-full" data-testid="services-reel">
            <div className="marquee-track">
              {[...SERVICE_CARDS, ...SERVICE_CARDS].map((card, i) => (
                <ServiceCard key={i} card={card} index={i % SERVICE_CARDS.length} />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* SOCIAL PROOF — Stats */}
      <section className="pt-24 md:pt-32" data-testid="social-proof-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-16 border-b border-[#1E1E2E]">
            <AnimatedStat value={3} suffix="x" label="faster quote response" />
            <AnimatedStat value={47} suffix="%" label="more booked jobs" />
            <AnimatedStat value={4.2} suffix="x" label="more reviews in 60 days" />
          </div>
        </div>
      </section>

      {/* LIFESTYLE BAND */}
      <section
        className="relative w-full overflow-hidden h-[280px] md:h-[400px]"
        data-testid="lifestyle-band-section"
      >
        <img
          src="https://cdn.searchlegends.com/how-it-works/home-services-pro.jpg"
          alt="Professional home service worker"
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to right, rgba(10,10,15,0.85) 0%, rgba(10,10,15,0.4) 60%, rgba(10,10,15,0.1) 100%)" }}
        />
        <div className="relative z-10 h-full flex items-center">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div style={{ maxWidth: "560px" }}>
              <p className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold mb-3">
                Real Businesses. Real Results.
              </p>
              <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-white leading-tight">
                The best operators don't wait for business. They build systems that bring it.
              </h2>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32" data-testid="testimonials-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] text-center mb-12">
              Built for the people doing the work.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "I booked 3 jobs in the first week that I know I would have lost before. The follow-up alone paid for itself.",
                name: "Jake M.",
                role: "Exterior Cleaning",
                featured: false,
                img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face",
              },
              {
                quote: "We went from 12 Google reviews to 47 in two months. CleanBid just asked at the right time.",
                name: "Maria S.",
                role: "Residential Cleaning",
                featured: true,
                img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=60&h=60&fit=crop&crop=face",
              },
              {
                quote: "It's the first piece of software that actually feels like it's on my side.",
                name: "Derek P.",
                role: "Lawn Care",
                featured: false,
                img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face",
              },
            ].map((t, i) => (
              <FadeUp key={i} delay={i * 100}>
                <CardHover className="h-full">
                <div
                  data-testid={`testimonial-${i}`}
                  className={`bg-[#111118] rounded-2xl p-8 h-full flex flex-col justify-between transition-[border-color,box-shadow] duration-300 ${
                    t.featured
                      ? "border-2 border-[#71DED6]/40 shadow-glow-green"
                      : "border border-[#1E1E2E]"
                  }`}
                >
                  <div>
                    <div className="flex gap-0.5 mb-4">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-[#F4F4F8] leading-relaxed">"{t.quote}"</p>
                  </div>
                  <div className="flex items-center gap-3 mt-6 pt-6 border-t border-[#1E1E2E]">
                    <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover bg-[#1E1E2E]" />
                    <div>
                      <p className="text-sm font-semibold text-[#F4F4F8]">{t.name}</p>
                      <p className="text-xs text-[#8B8BA7]">{t.role}</p>
                    </div>
                  </div>
                </div>
                </CardHover>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="py-20 md:py-28" data-testid="mid-cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <FadeUp>
            <div className="relative bg-[#111118] border border-[#1E1E2E] rounded-3xl p-12 md:p-16 text-center overflow-hidden">
              <div className="absolute inset-0 glow-cta pointer-events-none" />
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] relative z-10">
                Stop losing jobs to whoever follows up faster.
              </h2>
              <div className="mt-8 relative z-10 flex flex-col items-center gap-4">
                <a
                  href={CALCOM_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mid-cta-button"
                  className="bg-[#71DED6] text-[#10151D] font-semibold px-10 py-4 rounded-full text-lg hover:scale-[1.02] hover:shadow-glow-green transition-transform duration-200 inline-block"
                >
                  Book a Demo
                </a>
                <Link
                  to="/roi-calculator"
                  data-testid="mid-cta-roi-link"
                  className="text-sm text-[#8B8BA7] hover:text-[#71DED6] transition-colors duration-200 flex items-center gap-1"
                >
                  Not sure yet? Run the numbers
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="py-24 md:py-32" data-testid="pricing-preview-section">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <FadeUp className="text-center mb-4">
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">Pricing</span>
          </FadeUp>
          <FadeUp delay={60}>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] text-center mb-14">
              Simple pricing. Serious results.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: "Starter",
                price: "$79",
                tagline: "For solo operators just getting started.",
                features: ["1 user", "Up to 100 quotes/mo", "SMS quoting", "Basic follow-ups", "Review automation", "Email support"],
                cta: "Get Started",
                popular: false,
              },
              {
                name: "Growth",
                price: "$179",
                tagline: "The most popular plan for growing teams.",
                features: ["Up to 3 users", "Unlimited quotes", "Advanced automation", "Pipeline view", "Priority support", "All Starter features"],
                cta: "Get Started",
                popular: true,
              },
              {
                name: "Pro",
                price: "$349",
                tagline: "For multi-location teams and franchises.",
                features: ["Unlimited users", "Unlimited everything", "Custom integrations", "API access", "Dedicated support", "All Growth features"],
                cta: "Get Started",
                popular: false,
              },
            ].map((plan, i) => (
              <FadeUp key={i} delay={i * 120}>
                <CardHover className="h-full">
                <div
                  data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                  className={`bg-[#111118] rounded-3xl p-8 flex flex-col relative overflow-hidden h-full transition-[border-color,box-shadow] duration-300 ${
                    plan.popular
                      ? "border-2 border-[#71DED6]/50 shadow-glow-green"
                      : "border border-[#1E1E2E]"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute top-5 right-5 bg-[#71DED6] text-[#10151D] text-xs font-bold px-3 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-[#8B8BA7] uppercase tracking-widest">{plan.name}</p>
                    <div className="flex items-end gap-1 mt-3 mb-2">
                      <span className="text-4xl font-bold text-[#F4F4F8]">{plan.price}</span>
                      <span className="text-[#8B8BA7] mb-1">/mo</span>
                    </div>
                    <p className="text-[#8B8BA7] text-sm mb-6">{plan.tagline}</p>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-center gap-2.5 text-sm text-[#F4F4F8]">
                          <Check className="w-4 h-4 text-[#71DED6] flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <a
                    href={CALCOM_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                    className={`mt-auto text-center py-3 rounded-full font-semibold text-sm transition-all duration-200 ${
                      plan.popular
                        ? "bg-[#71DED6] text-[#10151D] hover:scale-[1.02] hover:shadow-glow-green-sm"
                        : "border border-[#1E1E2E] text-[#F4F4F8] hover:bg-[#1E1E2E]"
                    }`}
                  >
                    {plan.cta}
                  </a>
                  <a
                    href={getAuthSignUpURL()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`pricing-signup-${plan.name.toLowerCase()}`}
                    data-event={SIGNUP_EVENT}
                    className="block text-center text-xs text-[#8B8BA7] hover:text-[#F4F4F8] transition-colors mt-3"
                  >
                    or Sign Up directly →
                  </a>
                </div>
                </CardHover>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={240} className="text-center mt-8">
            <Link to="/pricing" className="text-sm text-[#8B8BA7] hover:text-[#F4F4F8] transition-colors underline underline-offset-4">
              View full pricing details
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="relative py-24 md:py-32 bg-[#111118]/30 overflow-hidden" data-testid="bottom-cta-section">
        {/* Radial glow */}
        <div
          className="absolute pointer-events-none z-0"
          style={{
            width: "700px",
            height: "700px",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "radial-gradient(circle at center, rgba(113,222,214,0.08) 0%, transparent 70%)",
          }}
        />
        {/* Brand mark — decorative, behind headline and button */}
        <img
          src="/cleanbid-logo-transparent.png"
          alt=""
          aria-hidden="true"
          className="absolute z-0 pointer-events-none select-none w-[300px] h-[300px] md:w-[560px] md:h-[560px] opacity-[0.06]"
          style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
        />
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center relative z-10">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] mb-4">
              Your competition is already following up.
            </h2>
          </FadeUp>
          <FadeUp delay={80}>
            <p className="text-lg text-[#8B8BA7] max-w-2xl mx-auto leading-relaxed mb-10">
              The service businesses winning in your market aren't working harder — they just have a system. CleanBid is that system.
            </p>
          </FadeUp>
          <FadeUp delay={160}>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bottom-cta-button"
              className="bg-[#71DED6] text-[#10151D] font-semibold px-12 py-4 rounded-full text-lg hover:scale-[1.02] hover:shadow-glow-green transition-transform duration-200 inline-block"
            >
              Book Your Demo
            </a>
            <p className="text-sm text-[#8B8BA7] mt-4">
              No pressure. 20 minutes. We'll show you exactly how it works for your business.
            </p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
